import { Image, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFPage from './PDFDocument';
import React from 'react';

const FONT_SIZE = 7;
const ROWS_PER_PAGE = 15;

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const styles = StyleSheet.create({
  tableContainer: {
    width: '100%',
    backgroundColor: '#ffffff',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#4F5F4B',
  },
  tableCol: {
    flex: 1,
    padding: 2,
    minWidth: 40,
  },
  tableCellHeader: {
    fontSize: FONT_SIZE,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
  },
  tableCell: {
    fontSize: FONT_SIZE,
    textAlign: 'center',
  },
  tableCellCurrency: {
    fontSize: FONT_SIZE,
    textAlign: 'center',
  },
  imageContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartImage: {
    width: '100%',
    height: 'auto',
    maxWidth: 320,
  },
});


const AssetsByYearPDF = ({ chartImages = {}, clientData, pageNumber, totalPages }) => {
  const projectionData = clientData?.assetsByYearData || [];
  const chartImage = chartImages['assets-by-year-chart'];

  // Split data into chunks to prevent PDF renderer from hanging
  const dataChunks = [];
  for (let i = 0; i < projectionData.length; i += ROWS_PER_PAGE) {
    dataChunks.push(projectionData.slice(i, i + ROWS_PER_PAGE));
  }

  return (
    <>
      {dataChunks.map((chunk, chunkIndex) => (
        <PDFPage
          key={chunkIndex}
          header={`Assets By Year${dataChunks.length > 1 ? ` (${chunkIndex + 1}/${dataChunks.length})` : ''}`}
          pageNumber={pageNumber + chunkIndex}
          totalPages={totalPages}
          showDisclaimer={true}
          showFooter={true}
        >
          <View style={styles.tableContainer}>
            <View style={styles.table}>
              {/* Table Header */}
              <View style={styles.tableHeaderRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Year</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>{clientData.firstname} Age</Text>
                </View>
                {clientData.spouse_firstname && (
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellHeader}>{clientData.spouse_firstname} Age</Text>
                  </View>
                )}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 1</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 2</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 3</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>New Contributions</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Growth</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Emergency</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Total</Text>
                </View>
              </View>

              {/* Table Body */}
              {chunk.map((row, index) => (
                <View key={index} style={[
                  styles.tableRow,
                  { backgroundColor: index % 2 === 0 ? '#fff' : '#eaeaea' }
                ]}>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.year}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCell}>{row.yearAge}</Text>
                  </View>
                  {clientData.spouse_firstname && (
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>{row.yearSpouseAge}</Text>
                    </View>
                  )}
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg1Principal)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg2Principal)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg3Principal)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.newContributions)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.growthPrincipal)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(clientData.emergencyFunds)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearTotalAssets)}</Text>
                  </View>
                </View>
              ))}
            </View>

            {/* Show chart only on last page */}
            {chunkIndex === dataChunks.length - 1 && chartImage && (
              <View style={styles.imageContainer}>
                <Image src={chartImage} style={styles.chartImage} />
              </View>
            )}
          </View>
        </PDFPage>
      ))}
    </>
  );
};

export default AssetsByYearPDF;
